import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, Save, Send, Settings2, MapPinOff, AlertTriangle, WifiOff } from 'lucide-react';
import {
  AUTOSAVE_DEBOUNCE_MS,
  createAutosaveScheduler,
  hasInspectionContent,
  type AutosaveEligibility,
  type AutosaveScheduler,
} from '../lib/inspectionAutosave';
import {
  localIdFor,
  saveLocalDraft,
  loadLocalDraft,
  deleteLocalDraft,
  type LocalDraftPatch,
} from '../lib/localDraftStore';
import { syncDraft, subscribePhotoResolved, notifyPendingChanged } from '../lib/inspectionSync';
import { rekeyPhotoGeo, type PhotoGeoMap } from '../lib/photoLocation';
import { InspectionMapButton } from './InspectionMapModal';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { ControllerInspectionForm } from './ControllerInspectionForm';
import { DoubleCheckInspectionForm } from './DoubleCheckInspectionForm';
import { ZonesInspectionForm } from './ZonesInspectionForm';
import { JobSiteDevices } from './JobSiteDevices';
import { ConfirmModal, type ErrorContext } from './ConfirmModal';
import { InspectionSummaryModal } from './InspectionSummaryModal';
import { InspectionReportValidationError, type ValidationErrorItem } from './InspectionReportValidationError';
import { WaterRestrictionPolicyCard } from './WaterRestrictionPolicyCard';
import { LocationTrackingIndicator } from './LocationTrackingIndicator';
import { ReportStatus } from '../types/inspection';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import { logEvent } from '../lib/telemetry';
import {
  useInspectionLocationHeartbeat,
  checkGeolocationPermission,
  requestLocationPermission,
  type HeartbeatResult,
} from '../lib/locationHeartbeat';

// Tech-facing location permission UI (banner + prompt). Kept behind a flag so it
// can be quickly disabled if a device gets stuck (e.g. a hard-denied iPad looping
// on failed captures). The heartbeat itself is always passive — it only reads
// location on devices that have ALREADY granted it and never prompts on its own.
const SHOW_LOCATION_PERMISSION_UI: boolean = true;

/** Consecutive failed foreground autosaves before the form stops auto-pushing. */
const AUTOSAVE_MAX_FAILURES = 4;

interface WorkCodeTranslation {
  work_code_id: string;
  locale: string;
  translated_code: string;
  translated_description: string;
}

interface FailureCodeRow {
  id: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
}

interface WorkCodeServer {
  id: string;
  code: string;
  description: string;
  is_default: boolean | null;
  is_issue: boolean | null;
}

interface WorkCodeRow {
  id: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
}

interface WorkCodeTypeRow {
  work_code_id: string;
  type_name: string;
}

interface InspectionReportWorkflowProps {
  jobSiteId: string;
  jobSiteName: string;
  reportId?: string;
  /** Aspire WorkTicket this inspection was started from (scheduled card), if any. */
  aspireWorkTicketId?: number | null;
  aspireWorkTicketNumber?: number | null;
  onBack: () => void;
}

type InspectionTab = 'controllers' | 'doublechecks' | 'zones' | 'manage';

interface ControllerData {
  id: string;
  name: string;
  controller_number: number;
  location: string;
  photo_url: string | null;
  zones: Array<{
    id: string;
    zone_number: number;
    type: string;
    location: string;
  }>;
}

interface ServerControllerRow {
  id: string;
  job_site_id: string | null;
  controller_number: number;
  description: string | null;
  location: string | null;
  photo_url: string | null;
}

interface ServerZoneRow {
  id: string;
  controller_id: string | null;
  zone_number: number;
  type: string | null;
  location: string | null;
}

interface DoubleCheckData {
  id: string;
  description: string;
  size: number;
  photo_url: string | null;
}

interface ServerDoubleCheckRow {
  id: string;
  job_site_id: string | null;
  description: string | null;
  size: number | string | null;
  photo_url: string | null;
}

interface ControllerInspection {
  controller_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
  /** Where each photo was taken, keyed by its URL (or offline marker). */
  photo_geo?: PhotoGeoMap;
}

interface DoubleCheckInspection {
  doublecheck_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
  /** Where each photo was taken, keyed by its URL (or offline marker). */
  photo_geo?: PhotoGeoMap;
}

interface ZoneIssueEntry {
  tempId: string;
  work_code: string;
  notes: string;
  photo_urls: string[];
  /** Where each photo was taken, keyed by its URL (or offline marker). */
  photo_geo?: PhotoGeoMap;
}

interface ZoneInspection {
  zone_id: string;
  issues: ZoneIssueEntry[];
}

interface ServerZoneInspectionRow {
  id: string;
  zone_id: string | null;
  controller_id: string | null;
  zone_number: number;
  type: string | null;
}

interface ServerZoneIssueRow {
  id: string;
  zone_inspection_id: string;
  work_code: string;
  notes: string | null;
  photo_urls: string[] | null;
  photo_geo?: Record<string, unknown> | null;
}

interface ServerControllerInspectionRow {
  id: string;
  controller_id: string | null;
  failure_codes: string[] | null;
  notes: string | null;
  photo_urls: string[] | null;
  photo_geo?: Record<string, unknown> | null;
}

interface ServerDoubleCheckInspectionRow {
  id: string;
  doublecheck_id: string | null;
  failure_codes: string[] | null;
  notes: string | null;
  photo_urls: string[] | null;
  photo_geo?: Record<string, unknown> | null;
}

export function InspectionReportWorkflow({
  jobSiteId,
  jobSiteName,
  reportId,
  aspireWorkTicketId,
  aspireWorkTicketNumber,
  onBack,
}: InspectionReportWorkflowProps) {
  const { t, i18n } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const [currentTab, setCurrentTab] = useState<InspectionTab>('controllers');
  const [loading, setLoading] = useState(true);
  // Once we've created (or were handed) a report, every subsequent save in
  // this workflow must PUT against the same id — otherwise repeated clicks
  // create duplicate report rows.
  const [activeReportId, setActiveReportId] = useState<string | undefined>(reportId);

  const [controllers, setControllers] = useState<ControllerData[]>([]);
  const [doubleChecks, setDoubleChecks] = useState<DoubleCheckData[]>([]);
  const [controllerFailureCodes, setControllerFailureCodes] = useState<FailureCodeRow[]>([]);
  const [doublecheckFailureCodes, setDoublecheckFailureCodes] = useState<FailureCodeRow[]>([]);
  const [workCodes, setWorkCodes] = useState<WorkCodeRow[]>([]);
  const [reportStatuses, setReportStatuses] = useState<ReportStatus[]>([]);
  const [translations, setTranslations] = useState<WorkCodeTranslation[]>([]);

  const [controllerInspections, setControllerInspections] = useState<ControllerInspection[]>([]);
  const [doublecheckInspections, setDoublecheckInspections] = useState<DoubleCheckInspection[]>([]);
  const [zoneInspections, setZoneInspections] = useState<ZoneInspection[]>([]);

  const [expandedControllers, setExpandedControllers] = useState<Set<string>>(new Set());
  const [expandedDoubleChecks, setExpandedDoubleChecks] = useState<Set<string>>(new Set());
  const [expandedZoneControllers, setExpandedZoneControllers] = useState<Set<string>>(new Set());
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());

  const [devicesChanged, setDevicesChanged] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved' | 'saved-local' | 'error'>('idle');
  const [restoredFromDevice, setRestoredFromDevice] = useState(false);
  // Stable per property+month so the local draft is re-found across reloads.
  const reportMonthRef = useRef(new Date().toISOString().slice(0, 7));
  const localIdRef = useRef(localIdFor(jobSiteId, reportMonthRef.current));
  // Set true only by genuine user edits (not by loadExistingReport populating
  // state), so autosave never writes loaded/empty state over a draft.
  const userEditedRef = useRef(false);
  const eligibilityRef = useRef<AutosaveEligibility>({
    userEdited: false, isSaving: false, hasContent: false, hasReport: false,
  });
  const saveRef = useRef<() => void>(() => {});
  const schedulerRef = useRef<AutosaveScheduler | null>(null);
  // Foreground-autosave circuit breaker. After this many consecutive failed
  // server pushes, the form stops auto-pushing (the background sync engine, with
  // its own backoff, keeps retrying) — so a permanently-failing save can't loop
  // every few seconds forever. Work is still persisted locally on each attempt.
  // Reset by a successful push or a manual Save/Submit.
  const autosaveFailuresRef = useRef(0);
  const autosavePausedRef = useRef(false);
  const loopBugFiledRef = useRef(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCtx, setErrorCtx] = useState<ErrorContext | undefined>(undefined);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);

  // Live location heartbeat: while this workflow is open, periodically record
  // the technician's position so managers can see them on the Inspections
  // Dashboard map and trace their path. If a heartbeat detects that location is
  // off/declined, nudge the tech to enable it (with a cooldown so it re-asks
  // periodically rather than every 45s).
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [locationPromptLoading, setLocationPromptLoading] = useState(false);
  // null = not determined yet. 'off' = location unavailable/not yet allowed (a
  // prompt can still help). 'denied' = hard-blocked (needs a settings change).
  const [gpsStatus, setGpsStatus] = useState<'ok' | 'off' | 'denied' | null>(null);
  // Distance (m) from the configured property when the tech is confirmed to be
  // inspecting somewhere other than that property; null when on-property/unknown.
  const [outOfBoundsM, setOutOfBoundsM] = useState<number | null>(null);
  // Connectivity has prevented the inspection from uploading to the server (the
  // work is safe on the device). Drives a persistent top-of-form banner; cleared
  // the moment a server push succeeds. `showOfflineModal` is the one-time, can't-
  // miss popup raised when the autosave circuit breaker first trips — the silent
  // autosave failure (the connection icon alone) is too easy for a tech to miss.
  const [offlineSaveBlocked, setOfflineSaveBlocked] = useState(false);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  // Sticky across passive heartbeats: once we KNOW it's denied, don't downgrade
  // back to 'off' (iOS heartbeats report 'not_granted' since they never re-ask).
  const gpsDeniedRef = useRef(false);
  const locationDismissedAtRef = useRef(0);
  const LOCATION_REPROMPT_COOLDOWN_MS = 3 * 60_000;

  const handleHeartbeatResult = useCallback((result: HeartbeatResult) => {
    if (result.ok) {
      gpsDeniedRef.current = false;
      setGpsStatus('ok');
      setShowLocationPrompt(false);
      setOutOfBoundsM(result.outOfBounds ? (result.distanceM ?? 0) : null);
      return;
    }
    if (result.errorName === 'permission_denied') {
      // Hard-blocked — a prompt won't help, so don't pop the modal; the banner
      // explains how to re-enable it.
      gpsDeniedRef.current = true;
      setGpsStatus('denied');
      setShowLocationPrompt(false);
      return;
    }
    const isOff =
      result.errorName === 'not_granted' ||
      result.errorName === 'position_unavailable' ||
      result.errorName === 'timeout' ||
      result.errorName === 'unavailable';
    if (!isOff) return;
    if (gpsDeniedRef.current) {
      setGpsStatus('denied');
      return;
    }
    setGpsStatus('off');
    if (!SHOW_LOCATION_PERMISSION_UI) return;
    if (Date.now() - locationDismissedAtRef.current < LOCATION_REPROMPT_COOLDOWN_MS) return;
    setShowLocationPrompt(true);
  }, []);

  // The property's center, used as the geofence anchor so live tracking stops
  // once the tech drives off the site. Best-effort: if it can't be read, the
  // tracker simply skips geofencing.
  const [anchor, setAnchor] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ gps_location: unknown }>(`/api/job-sites/${jobSiteId}`)
      .then((js) => {
        const loc = js?.gps_location as { latitude?: unknown; longitude?: unknown } | null;
        if (
          !cancelled &&
          loc &&
          typeof loc.latitude === 'number' &&
          typeof loc.longitude === 'number'
        ) {
          setAnchor({ lat: loc.latitude, lng: loc.longitude });
        }
      })
      .catch(() => {
        /* no anchor → tracker skips geofencing */
      });
    return () => {
      cancelled = true;
    };
  }, [jobSiteId]);

  useInspectionLocationHeartbeat(
    {
      reportId: activeReportId ?? reportId ?? null,
      jobSiteId,
      jobSiteName,
      anchorLat: anchor?.lat ?? null,
      anchorLng: anchor?.lng ?? null,
    },
    handleHeartbeatResult,
  );

  // If the browser can tell us location is in a "prompt"/"denied" state up front,
  // surface the nudge immediately rather than waiting for the first heartbeat.
  // (Safari reports "unknown" — there we rely on the heartbeat-failure path.)
  useEffect(() => {
    if (!SHOW_LOCATION_PERMISSION_UI) return;
    let cancelled = false;
    void checkGeolocationPermission().then((state) => {
      if (!cancelled && (state === 'prompt' || state === 'denied')) setShowLocationPrompt(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnableLocation = async () => {
    setLocationPromptLoading(true);
    const outcome = await requestLocationPermission();
    setLocationPromptLoading(false);
    setShowLocationPrompt(false);
    if (outcome === 'granted') {
      gpsDeniedRef.current = false;
      setGpsStatus('ok');
      return;
    }
    if (outcome === 'denied') {
      // Now we know for certain it's blocked — switch the banner to the
      // how-to-fix message instead of nudging a prompt that can't succeed.
      gpsDeniedRef.current = true;
      setGpsStatus('denied');
    }
    // Cooldown so the next heartbeat doesn't immediately re-pop the prompt.
    locationDismissedAtRef.current = Date.now();
  };

  const handleDismissLocationPrompt = () => {
    setShowLocationPrompt(false);
    locationDismissedAtRef.current = Date.now();
  };

  // Tapping the location badge asks for the explained modal — NOT the browser
  // prompt. requestLocationPermission is only ever reached from the modal's
  // Allow button (handleEnableLocation), i.e. a second, deliberate user tap.
  const handleShowLocationExplain = () => {
    if (!SHOW_LOCATION_PERMISSION_UI) return;
    locationDismissedAtRef.current = 0; // an explicit tap clears the snooze
    setShowLocationPrompt(true);
  };

  useEffect(() => {
    void logEvent('inspection_workflow_opened', `Opened inspection workflow`, {
      metadata: { job_site_id: jobSiteId, job_site_name: jobSiteName, report_id: reportId ?? null },
    });
    let cancelled = false;
    (async () => {
      await loadInspectionData();
      if (reportId) await loadExistingReport();
      // Restore unsynced local edits last — a dirty local draft holds work the
      // server hasn't seen yet (offline edits / a failed sync), so it wins.
      const local = await loadLocalDraft(localIdRef.current);
      if (cancelled || !local || !local.dirty) return;
      setControllerInspections(local.controllerInspections);
      setDoublecheckInspections(local.doublecheckInspections);
      setZoneInspections(local.zoneInspections);
      if (local.serverReportId) setActiveReportId(local.serverReportId);
      userEditedRef.current = true; // keep autosaving/syncing the restored work
      setRestoredFromDevice(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [jobSiteId, reportId, jobSiteName]);

  // Swap a local-photo:// marker for the real URL in the open form when the sync
  // engine uploads that queued photo (from this or a background sync).
  useEffect(() => {
    return subscribePhotoResolved((marker, url) => {
      const swap = (urls: string[]) => urls.map((u) => (u === marker ? url : u));
      // photo_geo is keyed by the same string, so it must move with the URL. Miss
      // this and a photo taken offline silently loses its pin the moment it
      // uploads -- the in-memory twin of replaceMarkerEverywhere.
      const swapGeo = (geo: PhotoGeoMap | undefined) =>
        geo && geo[marker] ? rekeyPhotoGeo(geo, marker, url) : geo;
      setControllerInspections((prev) => prev.map((c) => ({ ...c, photo_urls: swap(c.photo_urls), photo_geo: swapGeo(c.photo_geo) })));
      setDoublecheckInspections((prev) => prev.map((d) => ({ ...d, photo_urls: swap(d.photo_urls), photo_geo: swapGeo(d.photo_geo) })));
      setZoneInspections((prev) =>
        prev.map((z) => ({ ...z, issues: z.issues.map((i) => ({ ...i, photo_urls: swap(i.photo_urls), photo_geo: swapGeo(i.photo_geo) })) })),
      );
    });
  }, []);

  useEffect(() => {
    const loadTranslations = async () => {
      if (i18n.language === 'en') {
        setTranslations([]);
        return;
      }
      try {
        const all = await apiClient.get<WorkCodeTranslation[]>('/api/work-code-translations');
        setTranslations(all.filter(t => t.locale === i18n.language));
      } catch (err) {
        console.error('Failed to load translations:', err);
        setTranslations([]);
      }
    };
    loadTranslations();
  }, [i18n.language]);

  const loadInspectionData = async () => {
    setLoading(true);
    try {
      const [
        allControllers,
        allZones,
        allDoubleChecks,
        allWorkCodes,
        workCodeTypes,
        statuses,
      ] = await Promise.all([
        apiClient.get<ServerControllerRow[]>('/api/controllers'),
        apiClient.get<ServerZoneRow[]>('/api/zones'),
        apiClient.get<ServerDoubleCheckRow[]>('/api/double-checks'),
        apiClient.get<WorkCodeServer[]>('/api/work-codes'),
        apiClient.get<WorkCodeTypeRow[]>('/api/work-code-types'),
        apiClient.get<ReportStatus[]>('/api/report-statuses'),
      ]);

      const myControllers = allControllers
        .filter(c => c.job_site_id === jobSiteId)
        .sort((a, b) => a.controller_number - b.controller_number);

      const zonesByController = new Map<string, ServerZoneRow[]>();
      for (const z of allZones) {
        if (!z.controller_id) continue;
        const list = zonesByController.get(z.controller_id) ?? [];
        list.push(z);
        zonesByController.set(z.controller_id, list);
      }

      const formattedControllers: ControllerData[] = myControllers.map(c => ({
        id: c.id,
        name: c.description ?? '',
        controller_number: c.controller_number,
        location: c.location ?? '',
        photo_url: c.photo_url,
        zones: (zonesByController.get(c.id) ?? [])
          .sort((a, b) => a.zone_number - b.zone_number)
          .map(z => ({
            id: z.id,
            zone_number: z.zone_number,
            type: z.type ?? '',
            location: z.location ?? '',
          })),
      }));
      setControllers(formattedControllers);
      setExpandedControllers(new Set(formattedControllers.map(c => c.id)));
      setExpandedZoneControllers(new Set());
      setExpandedZones(new Set());

      const myDoubleChecks: DoubleCheckData[] = allDoubleChecks
        .filter(dc => dc.job_site_id === jobSiteId)
        .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''))
        .map(dc => ({
          id: dc.id,
          description: dc.description ?? '',
          size: typeof dc.size === 'number' ? dc.size : parseFloat(String(dc.size ?? '0')),
          photo_url: dc.photo_url,
        }));
      setDoubleChecks(myDoubleChecks);
      setExpandedDoubleChecks(new Set(myDoubleChecks.map(dc => dc.id)));

      const typeIdsFor = (typeName: string) =>
        new Set(workCodeTypes.filter(t => t.type_name === typeName).map(t => t.work_code_id));
      const zoneTypeIds = typeIdsFor('Zone');
      const controllerTypeIds = typeIdsFor('Controller');
      const doubleCheckTypeIds = typeIdsFor('DoubleCheck');
      const normalize = <T extends { is_default: boolean | null; is_issue: boolean | null }>(rows: T[]) =>
        rows.map(r => ({ ...r, is_default: r.is_default ?? false, is_issue: r.is_issue ?? false }));
      setWorkCodes(normalize(allWorkCodes.filter(wc => zoneTypeIds.has(wc.id))));
      setControllerFailureCodes(normalize(allWorkCodes.filter(wc => controllerTypeIds.has(wc.id))));
      setDoublecheckFailureCodes(normalize(allWorkCodes.filter(wc => doubleCheckTypeIds.has(wc.id))));
      setReportStatuses(statuses);
    } catch (error) {
      console.error('Error loading inspection data:', error);
      setErrorMessage(t('workflow.errorLoadInspection'));
      setErrorCtx({ error, componentName: 'InspectionReportWorkflow', action: 'loadInspectionData', pageContext: 'inspection-form' });
    } finally {
      setLoading(false);
    }
  };

  const loadExistingReport = async () => {
    if (!reportId) return;

    try {
      const [ciRows, diRows, ziRows, zissueRows] = await Promise.all([
        apiClient.get<ServerControllerInspectionRow[]>(`/api/controller-inspections?reportId=${reportId}`),
        apiClient.get<ServerDoubleCheckInspectionRow[]>(`/api/doublecheck-inspections?reportId=${reportId}`),
        apiClient.get<ServerZoneInspectionRow[]>(`/api/zone-inspections?reportId=${reportId}`),
        apiClient.get<ServerZoneIssueRow[]>(`/api/zone-issues?reportId=${reportId}`),
      ]);

      setControllerInspections(
        ciRows
          .filter(ci => ci.controller_id)
          .map(ci => ({
            controller_id: ci.controller_id!,
            failure_codes: ci.failure_codes ?? [],
            notes: ci.notes ?? '',
            photo_urls: ci.photo_urls ?? [],
            photo_geo: (ci.photo_geo ?? {}) as PhotoGeoMap,
          })),
      );

      setDoublecheckInspections(
        diRows
          .filter(di => di.doublecheck_id)
          .map(di => ({
            doublecheck_id: di.doublecheck_id!,
            failure_codes: di.failure_codes ?? [],
            notes: di.notes ?? '',
            photo_urls: di.photo_urls ?? [],
            photo_geo: (di.photo_geo ?? {}) as PhotoGeoMap,
          })),
      );

      const issuesByZi = new Map<string, ServerZoneIssueRow[]>();
      for (const issue of zissueRows) {
        const list = issuesByZi.get(issue.zone_inspection_id) ?? [];
        list.push(issue);
        issuesByZi.set(issue.zone_inspection_id, list);
      }

      const grouped: { [zoneId: string]: ZoneInspection } = {};
      for (const zi of ziRows) {
        if (!zi.zone_id) continue;
        const issues: ZoneIssueEntry[] = (issuesByZi.get(zi.id) ?? []).map(issue => ({
          tempId: issue.id,
          work_code: issue.work_code,
          notes: issue.notes ?? '',
          photo_urls: issue.photo_urls ?? [],
          photo_geo: (issue.photo_geo ?? {}) as PhotoGeoMap,
        }));
        grouped[zi.zone_id] = { zone_id: zi.zone_id, issues };
      }
      setZoneInspections(Object.values(grouped));
    } catch (error) {
      console.error('Error loading existing report:', error);
      setErrorMessage(t('workflow.errorLoadExistingReport'));
      setErrorCtx({ error, componentName: 'InspectionReportWorkflow', action: 'loadExistingReport', pageContext: 'inspection-form' });
    }
  };

  const handleDevicesChanged = useCallback(() => {
    setDevicesChanged(true);
  }, []);

  const handleTabChange = useCallback((tab: InspectionTab) => {
    if (currentTab === 'manage' && tab !== 'manage' && devicesChanged) {
      loadInspectionData();
      if (reportId) {
        loadExistingReport();
      }
      setDevicesChanged(false);
    }
    if (tab !== currentTab) {
      void logEvent('inspection_tab_changed', `Tab ${currentTab} -> ${tab}`, {
        metadata: { from: currentTab, to: tab, report_id: activeReportId ?? reportId ?? null },
      });
    }
    setCurrentTab(tab);
  }, [currentTab, devicesChanged, reportId, activeReportId]);

  const validateInspection = (): ValidationErrorItem[] => {
    const errors: ValidationErrorItem[] = [];

    const inspectedControllerIds = new Set(
      controllerInspections
        .filter(ci => ci.failure_codes && ci.failure_codes.length > 0)
        .map(ci => ci.controller_id),
    );
    const uninspectedControllers = controllers.filter(c => !inspectedControllerIds.has(c.id));
    uninspectedControllers.forEach(controller => {
      errors.push({
        message: t('validation.controllerNotInspected', {
          n: controller.controller_number,
          location: controller.location,
        }),
        deviceType: 'controller',
        deviceId: controller.id,
      });
    });

    const inspectedDoubleCheckIds = new Set(
      doublecheckInspections
        .filter(di => di.failure_codes && di.failure_codes.length > 0)
        .map(di => di.doublecheck_id),
    );
    const uninspectedDoubleChecks = doubleChecks.filter(dc => !inspectedDoubleCheckIds.has(dc.id));
    uninspectedDoubleChecks.forEach(dc => {
      errors.push({
        message: t('validation.doubleCheckNotInspected', {
          size: dc.size,
          desc: dc.description,
        }),
        deviceType: 'doublecheck',
        deviceId: dc.id,
      });
    });

    const allZones = controllers.flatMap(c => c.zones.map(z => ({ ...z, controllerId: c.id })));
    const zonesWithValidWorkCodes = new Set(
      zoneInspections
        .filter(zi => zi.issues && zi.issues.length > 0 && zi.issues.every(issue => issue.work_code && issue.work_code.trim().length > 0))
        .map(zi => zi.zone_id),
    );
    const uninspectedZones = allZones.filter(z => !zonesWithValidWorkCodes.has(z.id));
    uninspectedZones.forEach(zone => {
      const controller = controllers.find(c => c.id === zone.controllerId);
      errors.push({
        message: t('validation.zoneNotInspected', {
          n: controller?.controller_number,
          z: zone.zone_number,
          location: zone.location,
        }),
        deviceType: 'zone',
        deviceId: zone.id,
        controllerId: zone.controllerId,
        zoneNumber: zone.zone_number,
      });
    });

    controllerInspections.forEach(inspection => {
      const controller = controllers.find(c => c.id === inspection.controller_id);
      if (!controller) return;
      const hasIssues = inspection.failure_codes.some(code => {
        const fc = controllerFailureCodes.find(f => f.code === code);
        return fc?.is_issue;
      });
      if (hasIssues && (!inspection.notes || inspection.notes.trim().length === 0)) {
        errors.push({
          message: t('validation.controllerNotesRequired', { n: controller.controller_number }),
          deviceType: 'controller',
          deviceId: inspection.controller_id,
        });
      }
    });

    doublecheckInspections.forEach(inspection => {
      const doubleCheck = doubleChecks.find(dc => dc.id === inspection.doublecheck_id);
      if (!doubleCheck) return;
      const hasIssues = inspection.failure_codes.some(code => {
        const fc = doublecheckFailureCodes.find(f => f.code === code);
        return fc?.is_issue;
      });
      if (hasIssues && (!inspection.notes || inspection.notes.trim().length === 0)) {
        errors.push({
          message: t('validation.doubleCheckNotesRequired', {
            size: doubleCheck.size,
            desc: doubleCheck.description,
          }),
          deviceType: 'doublecheck',
          deviceId: inspection.doublecheck_id,
        });
      }
    });

    zoneInspections.forEach(inspection => {
      const zone = controllers.flatMap(c => c.zones).find(z => z.id === inspection.zone_id);
      if (!zone) return;
      const controller = controllers.find(c => c.zones.some(z => z.id === inspection.zone_id));
      if (!controller) return;
      const hasIssues = inspection.issues.some(issue => {
        const wc = workCodes.find(w => w.code === issue.work_code);
        return wc?.is_issue;
      });
      if (hasIssues) {
        const issuesWithoutNotes = inspection.issues.filter(i => {
          const wc = workCodes.find(w => w.code === i.work_code);
          return wc?.is_issue && (!i.notes || i.notes.trim().length === 0);
        });
        if (issuesWithoutNotes.length > 0) {
          errors.push({
            message: t('validation.zoneNotesRequired', {
              n: controller.controller_number,
              z: zone.zone_number,
            }),
            deviceType: 'zone',
            deviceId: inspection.zone_id,
            controllerId: controller.id,
            zoneNumber: zone.zone_number,
          });
        }
      }
    });

    return errors;
  };

  // Snapshot the workflow state into a self-contained local draft (incl. the
  // reference data the server-push needs), so a background sync can push it even
  // when this component is unmounted.
  const buildLocalDraftPatch = (
    draftStatusId: string,
    submittedStatusId: string,
    pendingSubmit?: boolean,
  ): LocalDraftPatch => ({
    localId: localIdRef.current,
    serverReportId: activeReportId ?? reportId ?? null,
    jobSiteId,
    jobSiteName,
    month: reportMonthRef.current,
    aspireWorkTicketId: aspireWorkTicketId ?? null,
    aspireWorkTicketNumber: aspireWorkTicketNumber ?? null,
    controllerInspections,
    doublecheckInspections,
    zoneInspections,
    // Only set when the caller has an explicit intent (manual Save Draft /
    // Submit); silent autosaves omit it so a prior Submit intent is preserved.
    ...(pendingSubmit === undefined ? {} : { pendingSubmit }),
    refData: {
      controllers: controllers.map((c) => ({
        id: c.id,
        controller_number: c.controller_number,
        name: c.name,
        location: c.location ?? null,
        zones: c.zones.map((z) => ({ id: z.id, zone_number: z.zone_number, type: z.type ?? null })),
      })),
      doubleChecks: doubleChecks.map((d) => ({ id: d.id, description: d.description, size: d.size ?? null })),
      draftStatusId,
      submittedStatusId,
    },
  });

  // Once a report is submitted on the server, a User can no longer edit it — any
  // further PUT returns 403. Track that so autosave and re-saves stop touching it
  // (and never loop on 403s).
  const submittedRef = useRef(false);

  const saveReport = async (isDraft: boolean, opts?: { silent?: boolean }) => {
    if (isSaving) return;
    const silent = opts?.silent ?? false;
    // Already submitted — there's nothing left to push. Don't generate a 403;
    // confirm success for a manual tap, and no-op a silent autosave.
    if (submittedRef.current) {
      if (!silent) {
        setShowSummaryModal(false);
        setShowSaveDraftModal(false);
        setSuccessMessage(t('workflow.reportSubmitted'));
        setShowSuccessModal(true);
      }
      return;
    }
    // A manual Save/Submit is an explicit retry — clear any autosave circuit
    // breaker so the form attempts the server push again from a clean slate.
    if (!silent) {
      autosaveFailuresRef.current = 0;
      autosavePausedRef.current = false;
    }
    // A manual save/submit pre-empts any pending autosave.
    schedulerRef.current?.cancel();
    setIsSaving(true);
    if (silent) setAutosaveStatus('saving');
    const startedAt = Date.now();
    const formStateSnapshot = {
      is_draft: isDraft,
      existing_report_id: activeReportId ?? reportId ?? null,
      controllers_total: controllers.length,
      double_checks_total: doubleChecks.length,
      zones_total: controllers.reduce((sum, c) => sum + c.zones.length, 0),
      controller_inspections_state: controllerInspections.length,
      doublecheck_inspections_state: doublecheckInspections.length,
      zone_inspections_state: zoneInspections.length,
      zone_issues_state: zoneInspections.reduce((sum, zi) => sum + (zi.issues?.length || 0), 0),
    };
    if (!silent) {
      void logEvent('inspection_save_started', `Save started (is_draft=${isDraft})`, {
        metadata: formStateSnapshot,
      });
    }
    try {
      // NOTE: there used to be a getUserProfile() gate here. Its result was never
      // used — it only decided whether to abort with "not authenticated" — and it
      // did real damage: getUserProfile swallows EVERY failure into null, so a
      // transient server 500 (e.g. the Azure Postgres connection-timeout blips)
      // aborted the save with a bogus "you are not signed in", and it did so
      // BEFORE the local-draft write below, removing the safety net at the exact
      // moment the server was flaky. Real auth failures are handled properly by
      // the request itself: apiClient routes a genuine 401 through the re-auth
      // prompt, which saves a return breadcrumb and brings the tech back here.
      const draftStatus = reportStatuses.find(s => s.status_type === 'draft');
      const submittedStatus = reportStatuses.find(s => s.status_type === 'submitted');
      if (!draftStatus) throw new Error(t('workflow.errorStatusNotFound', { type: 'draft' }));
      if (!submittedStatus) throw new Error(t('workflow.errorStatusNotFound', { type: 'submitted' }));

      // The User-role permission rule on inspection child rows is "owner +
      // is_draft=true". So we MUST keep the report in draft state while we
      // write children, and only flip to submitted at the very end. Reversing
      // the order is how a non-admin loses access mid-save and ends up with a
      // submitted report that has zero (or stale) child rows.
      // 1. Always persist locally first — this survives offline / reload / crash.
      //    A manual Submit records the intent so a background sync pushes it as a
      //    SUBMITTED report on reconnect; a manual Save Draft clears that intent;
      //    a silent autosave leaves whatever intent was last set untouched.
      const submitIntent = silent ? undefined : !isDraft;
      await saveLocalDraft(buildLocalDraftPatch(draftStatus.id, submittedStatus.id, submitIntent));

      // 2. Offline: stop here. The work is safe on the device; the sync engine
      //    pushes it (uploading any queued photos) when connectivity returns.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        notifyPendingChanged(); // refresh the "waiting to upload" indicator now
        setOfflineSaveBlocked(true); // persistent reminder until it uploads
        if (silent) {
          setAutosaveStatus('saved-local');
        } else {
          setShowSummaryModal(false);
          setShowSaveDraftModal(false);
          setSuccessMessage(
            isDraft ? t('workflow.savedOfflineMessage') : t('workflow.submittedOfflineMessage'),
          );
          setShowSuccessModal(true);
        }
        return;
      }

      // 2b. Circuit breaker: if the foreground autosave has failed repeatedly,
      //     stop hammering the server from the form — the work is already saved
      //     locally (step 1) and the background sync engine, with its own backoff,
      //     owns retries. A manual Save/Submit (which cleared the flag above) or a
      //     later success resumes normal autosaving.
      if (silent && autosavePausedRef.current) {
        notifyPendingChanged();
        setAutosaveStatus('saved-local');
        return;
      }

      // 3. Online: upload queued photos, push to the server (same sequence as
      //    before, now in inspectionSync), and submit if asked.
      const reportData = {
        id: (await syncDraft(localIdRef.current, { isDraft })) ?? '',
        month: reportMonthRef.current,
      };
      if (reportData.id) setActiveReportId(reportData.id);
      // A push succeeded — clear the failure streak and resume normal autosaving,
      // and take down the "couldn't upload" banner (connectivity is back).
      autosaveFailuresRef.current = 0;
      autosavePausedRef.current = false;
      loopBugFiledRef.current = false;
      setOfflineSaveBlocked(false);

      // Skip the audit row for silent autosaves so debounced saves don't flood
      // audit_logs; manual Save/Submit still audits.
      if (!silent) {
        logAudit({
          action: isDraft
            ? (reportId ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE)
            : AUDIT_ACTIONS.SUBMIT,
          entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
          entityId: reportData.id,
          entityLabel: `${jobSiteName} - ${reportData.month}`,
          parentEntityType: AUDIT_ENTITIES.PROPERTY,
          parentEntityId: jobSiteId,
          parentEntityLabel: jobSiteName,
          details: {
            is_draft: isDraft,
            month: reportData.month,
            controller_inspections: controllerInspections.length,
            zone_inspections: zoneInspections.length,
            doublecheck_inspections: doublecheckInspections.length,
            zone_issues: zoneInspections.reduce((sum, zi) => sum + (zi.issues?.length || 0), 0),
          },
          pageContext: 'inspection-form',
        });
      }

      void logEvent(
        silent ? 'inspection_autosave_succeeded' : 'inspection_save_succeeded',
        `Save succeeded (is_draft=${isDraft}, silent=${silent})`,
        {
          metadata: {
            ...formStateSnapshot,
            report_id: reportData.id,
            duration_ms: Date.now() - startedAt,
          },
        },
      );

      // A submit just succeeded — lock the report so autosave/re-saves stop
      // touching it (the server now rejects edits from a User with 403).
      if (!isDraft) submittedRef.current = true;

      if (silent) {
        setAutosaveStatus('saved');
      } else {
        setShowSummaryModal(false);
        setShowSaveDraftModal(false);
        setSuccessMessage(isDraft ? t('workflow.draftSaved') : t('workflow.reportSubmitted'));
        setShowSuccessModal(true);
      }
    } catch (error) {
      const errMessage = error instanceof Error ? error.message : String(error);
      const errStatus = (error as { status?: unknown })?.status;
      const errCode = (error as { code?: unknown })?.code;
      void logEvent('inspection_save_failed', `Save failed (is_draft=${isDraft}): ${errMessage}`, {
        severity: 'error',
        metadata: {
          ...formStateSnapshot,
          duration_ms: Date.now() - startedAt,
          error_message: errMessage,
          error_status: errStatus,
          error_code: errCode,
        },
      });
      console.error('Error saving report:', error);
      // A 403 means the report is already submitted on the server (a User can't
      // edit a non-draft report) — so the submit DID go through. Treat it as
      // success: lock the report, drop the local draft so the sync engine stops
      // retrying, and confirm to the tech instead of showing "won't let me send".
      if (errStatus === 403) {
        submittedRef.current = true;
        try {
          await deleteLocalDraft(localIdRef.current);
        } catch {
          /* best-effort */
        }
        notifyPendingChanged();
        if (silent) {
          setAutosaveStatus('saved');
        } else {
          setShowSummaryModal(false);
          setShowSaveDraftModal(false);
          setSuccessMessage(t('workflow.reportSubmitted'));
          setShowSuccessModal(true);
        }
        return;
      }
      // Non-403 failure. Count consecutive silent-autosave failures; once we hit
      // the threshold, trip the circuit breaker (the form stops auto-pushing) and
      // file ONE deduped "stuck save loop" bug so it surfaces in triage — a
      // permanently-failing autosave used to loop invisibly forever (error
      // breadcrumbs, never a bug). The background sync engine keeps retrying.
      if (silent) {
        autosaveFailuresRef.current += 1;
        if (autosaveFailuresRef.current >= AUTOSAVE_MAX_FAILURES && !autosavePausedRef.current) {
          autosavePausedRef.current = true;
          // Sustained save failures (often the connection silently dropped while
          // the OS still reports "online"). The status icon alone is too easy to
          // miss, so raise a can't-miss popup once + a persistent banner telling
          // the tech their work is on the device and to reopen the app on signal.
          setOfflineSaveBlocked(true);
          setShowOfflineModal(true);
          if (!loopBugFiledRef.current) {
            loopBugFiledRef.current = true;
            // Stable message (no variable count or ids) so a storm dedupes to one bug.
            void logEvent(
              'inspection_save_loop_blocked',
              `Autosave stopped after repeated save failures (status ${errStatus ?? 'none'}, code ${errCode ?? 'none'})`,
              {
                severity: 'error',
                metadata: {
                  report_id: activeReportId ?? reportId ?? null,
                  local_id: localIdRef.current,
                  job_site_id: jobSiteId,
                  job_site_name: jobSiteName,
                  error_status: errStatus ?? null,
                  error_code: errCode ?? null,
                  error_message: errMessage,
                  consecutive_failures: autosaveFailuresRef.current,
                },
              },
            );
          }
        }
      }
      if (silent) {
        // Local draft is already persisted, so the work is safe; it stays queued
        // and the sync engine retries. Show "saved on device", not an alarm.
        setAutosaveStatus('saved-local');
      } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        // Signal dropped mid-save. The work is already persisted locally (step 1)
        // with the right Submit/Draft intent, so reassure rather than alarm — the
        // sync engine finishes it on reconnect.
        notifyPendingChanged();
        setOfflineSaveBlocked(true); // persistent reminder until it uploads
        setShowSummaryModal(false);
        setShowSaveDraftModal(false);
        setSuccessMessage(
          isDraft ? t('workflow.savedOfflineMessage') : t('workflow.submittedOfflineMessage'),
        );
        setShowSuccessModal(true);
      } else {
        setErrorMessage(t('workflow.errorSaveReport'));
        setErrorCtx({ error, componentName: 'InspectionReportWorkflow', action: 'saveReport', pageContext: 'inspection-form' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = () => {
    setShowSaveDraftModal(true);
  };

  const handleErrorClick = (error: ValidationErrorItem) => {
    void logEvent('inspection_validation_error_clicked', `Jumped to ${error.deviceType}`, {
      metadata: {
        device_type: error.deviceType,
        device_id: error.deviceId,
        controller_id: error.controllerId ?? null,
        zone_number: error.zoneNumber ?? null,
      },
    });
    if (error.deviceType === 'controller') {
      setCurrentTab('controllers');
      setExpandedControllers(new Set([error.deviceId]));
    } else if (error.deviceType === 'doublecheck') {
      setCurrentTab('doublechecks');
      setExpandedDoubleChecks(new Set([error.deviceId]));
    } else if (error.deviceType === 'zone') {
      setCurrentTab('zones');
      if (error.controllerId) setExpandedZoneControllers(new Set([error.controllerId]));
      setExpandedZones(new Set([error.deviceId]));
    }
  };

  const handleSubmitReport = () => {
    const errors = validateInspection();
    if (errors.length > 0) {
      void logEvent('inspection_submit_blocked', `Submit blocked by ${errors.length} validation error(s)`, {
        severity: 'warning',
        metadata: {
          existing_report_id: activeReportId ?? reportId ?? null,
          job_site_id: jobSiteId,
          errors_total: errors.length,
          errors_by_device: errors.reduce<Record<string, number>>((acc, e) => {
            acc[e.deviceType] = (acc[e.deviceType] ?? 0) + 1;
            return acc;
          }, {}),
          sample: errors.slice(0, 5).map((e) => ({ deviceType: e.deviceType, message: e.message })),
        },
      });
      setValidationErrors(errors);
      return;
    }
    setShowSummaryModal(true);
  };

  const handleConfirmSaveDraft = () => {
    saveReport(true);
  };

  const handleConfirmSubmit = () => {
    saveReport(false);
  };

  // ---- Autosave (restores the pre-migration draft autosave) -----------------
  // The child forms call these on every edit. Wrapping the raw setters lets us
  // distinguish genuine user edits from loadExistingReport's state population.
  const onControllerInspectionsEdited = (v: ControllerInspection[]) => {
    userEditedRef.current = true;
    setControllerInspections(v);
  };
  const onDoublecheckInspectionsEdited = (v: DoubleCheckInspection[]) => {
    userEditedRef.current = true;
    setDoublecheckInspections(v);
  };
  const onZoneInspectionsEdited = (v: ZoneInspection[]) => {
    userEditedRef.current = true;
    setZoneInspections(v);
  };

  // Keep the scheduler's eligibility + save callback current (read at fire time).
  eligibilityRef.current = {
    userEdited: userEditedRef.current,
    isSaving,
    hasContent: hasInspectionContent(controllerInspections, doublecheckInspections, zoneInspections),
    hasReport: Boolean(activeReportId ?? reportId),
  };
  saveRef.current = () => {
    void saveReport(true, { silent: true });
  };
  if (!schedulerRef.current) {
    schedulerRef.current = createAutosaveScheduler(
      () => eligibilityRef.current,
      () => saveRef.current(),
      AUTOSAVE_DEBOUNCE_MS,
    );
  }

  // Debounced autosave a few seconds after edits settle.
  useEffect(() => {
    schedulerRef.current?.onChange();
  }, [controllerInspections, doublecheckInspections, zoneInspections]);

  // Safety net: flush immediately when the tab is hidden/backgrounded or the
  // page unloads (app-switch, screen-lock, PWA close, version-mismatch reload).
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') schedulerRef.current?.flush();
    };
    const onPageHide = () => schedulerRef.current?.flush();
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onBack();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-theme-secondary">{t('workflow.loadingData')}</p>
      </div>
    );
  }

  const tabs: Array<{ id: InspectionTab; label: string; shortLabel: string; count?: number; icon?: boolean }> = [
    { id: 'controllers', label: t('workflow.tabControllers'), shortLabel: t('workflow.tabControllersShort'), count: controllers.length },
    { id: 'doublechecks', label: t('workflow.tabDoubleChecks'), shortLabel: t('workflow.tabDoubleChecksShort'), count: doubleChecks.length },
    { id: 'zones', label: t('workflow.tabZones'), shortLabel: t('workflow.tabZonesShort'), count: controllers.reduce((sum, c) => sum + c.zones.length, 0) },
    { id: 'manage', label: t('workflow.tabManage'), shortLabel: t('workflow.tabManageShort'), icon: true },
  ];

  const outOfBoundsLabel =
    outOfBoundsM == null
      ? ''
      : outOfBoundsM >= 1000
        ? `${(outOfBoundsM / 1000).toFixed(1)} km`
        : `${outOfBoundsM} m`;

  return (
    <div className="max-w-7xl mx-auto pb-20 md:pb-6">
      {outOfBoundsM != null && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">
            {t('locationBanner.outOfBounds', { property: jobSiteName, distance: outOfBoundsLabel })}
          </span>
        </div>
      )}
      {offlineSaveBlocked && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-400 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <WifiOff size={16} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">{t('workflow.offlineBlockedBanner')}</span>
        </div>
      )}
      {SHOW_LOCATION_PERMISSION_UI && gpsStatus === 'denied' ? (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          <MapPinOff size={16} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">{t('locationBanner.deniedMessage')}</span>
        </div>
      ) : SHOW_LOCATION_PERMISSION_UI && gpsStatus === 'off' ? (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          <MapPinOff size={16} className="flex-shrink-0" />
          <span className="flex-1">{t('locationBanner.message')}</span>
          <button
            onClick={handleEnableLocation}
            disabled={locationPromptLoading}
            className="font-semibold underline whitespace-nowrap disabled:opacity-50"
          >
            {t('locationBanner.action')}
          </button>
        </div>
      ) : null}
      <div className="mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 mb-1 text-sm"
        >
          <ChevronLeft size={18} />
          {t('workflow.back')}
        </button>
        <div className="flex items-center flex-wrap gap-2 mt-0.5">
          <h1 className="text-lg md:text-2xl font-bold text-theme-primary leading-tight">{jobSiteName}</h1>
          <InspectionMapButton jobSiteId={jobSiteId} jobSiteName={jobSiteName} />
          {/* Honest, quiet read-out of whether tracking is actually running. The
              badge only READS state (never prompts); its tap opens the explained
              modal below, which is the sole route to the browser prompt. Shown
              even when the permission-UI kill-switch is off — it can't prompt —
              but the tap is disabled there. */}
          <LocationTrackingIndicator
            blocked={gpsStatus === 'denied'}
            canRequest={SHOW_LOCATION_PERMISSION_UI}
            busy={locationPromptLoading}
            onExplainTap={handleShowLocationExplain}
          />
        </div>
        <WaterRestrictionPolicyCard jobSiteId={jobSiteId} className="mt-3" />
      </div>

      {restoredFromDevice && (
        <div className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          {t('workflow.restoredFromDevice')}
        </div>
      )}

      <div className="sticky top-0 z-10 -mx-4 px-4 bg-subtle pb-3 pt-1">
        <div className="bg-surface rounded-lg shadow-sm border border-line">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex-1 py-2.5 font-medium transition-colors text-xs md:text-sm relative ${
                  currentTab === tab.id
                    ? tab.id === 'manage'
                      ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10'
                      : 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10'
                    : 'text-theme-secondary active:bg-subtle'
                }`}
              >
                {tab.icon && <Settings2 size={14} className="inline-block mr-0.5 -mt-0.5 md:mr-1" />}
                <span className="md:hidden">{tab.shortLabel}</span>
                <span className="hidden md:inline">{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`ml-0.5 md:ml-1.5 px-1 md:px-1.5 py-0.5 text-[10px] md:text-xs rounded-full inline-block ${
                    currentTab === tab.id ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300' : 'bg-subtle text-theme-secondary'
                  }`}>
                    {tab.count}
                  </span>
                )}
                {currentTab === tab.id && (
                  <div className={`absolute bottom-0 left-2 right-2 h-0.5 rounded-full ${
                    tab.id === 'manage' ? 'bg-amber-500' : 'bg-blue-500'
                  }`} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        {currentTab === 'controllers' && (
          <ControllerInspectionForm
            controllers={controllers}
            inspections={controllerInspections}
            failureCodes={controllerFailureCodes}
            onInspectionChange={onControllerInspectionsEdited}
            expandedControllers={expandedControllers}
            onExpandedChange={setExpandedControllers}
            translations={translations}
            localDraftId={localIdRef.current}
          />
        )}

        {currentTab === 'doublechecks' && (
          <DoubleCheckInspectionForm
            doubleChecks={doubleChecks}
            inspections={doublecheckInspections}
            failureCodes={doublecheckFailureCodes}
            onInspectionChange={onDoublecheckInspectionsEdited}
            expandedDoubleChecks={expandedDoubleChecks}
            onExpandedChange={setExpandedDoubleChecks}
            translations={translations}
            localDraftId={localIdRef.current}
          />
        )}

        {currentTab === 'zones' && (
          <ZonesInspectionForm
            controllers={controllers}
            inspections={zoneInspections}
            workCodes={workCodes}
            onInspectionsChange={onZoneInspectionsEdited}
            expandedControllers={expandedZoneControllers}
            onExpandedControllersChange={setExpandedZoneControllers}
            expandedZones={expandedZones}
            onExpandedZonesChange={setExpandedZones}
            translations={translations}
            localDraftId={localIdRef.current}
          />
        )}

        {currentTab === 'manage' && (
          <div className="bg-surface rounded-xl shadow-sm border border-line p-4 md:p-6">
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {t('workflow.manageHint')}
              </p>
            </div>
            <JobSiteDevices
              jobSiteId={jobSiteId}
              jobSiteName={jobSiteName}
              onDataChange={handleDevicesChanged}
            />
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 md:static md:mt-4 bg-surface border-t md:border-t-0 border-line p-3 md:p-0 flex flex-col gap-1 z-20 md:bg-transparent shadow-[0_-2px_8px_rgba(0,0,0,0.08)] md:shadow-none">
        <div className="h-4 text-right text-xs leading-4" aria-live="polite">
          {autosaveStatus === 'saving' && <span className="text-theme-secondary">{t('workflow.autosaveSaving')}</span>}
          {autosaveStatus === 'saved' && <span className="text-green-600 dark:text-green-400">{t('workflow.autosaveSaved')}</span>}
          {autosaveStatus === 'saved-local' && <span className="text-amber-600 dark:text-amber-400">{t('workflow.autosaveSavedLocal')}</span>}
          {autosaveStatus === 'error' && <span className="text-red-600 dark:text-red-400">{t('workflow.autosaveError')}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="flex-1 flex items-center justify-center gap-2 py-3 md:py-3.5 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm active:scale-[0.98]"
          >
            <Save size={18} />
            <span>{t('workflow.saveDraftButton')}</span>
          </button>
          <button
            onClick={handleSubmitReport}
            disabled={isSaving}
            className="flex-1 flex items-center justify-center gap-2 py-3 md:py-3.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm active:scale-[0.98]"
          >
            <Send size={18} />
            <span>{t('workflow.submitButton')}</span>
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showSaveDraftModal}
        title={t('workflow.saveDraftTitle')}
        message={t('workflow.saveDraftMessage')}
        confirmText={t('workflow.saveDraftButton')}
        cancelText={tCommon('actions.cancel')}
        variant="success"
        onConfirm={handleConfirmSaveDraft}
        onCancel={() => setShowSaveDraftModal(false)}
        isLoading={isSaving}
      />

      {showSummaryModal && (
        <InspectionSummaryModal
          jobSiteName={jobSiteName}
          controllers={controllers}
          controllerInspections={controllerInspections}
          controllerFailureCodes={controllerFailureCodes}
          doubleChecks={doubleChecks}
          doublecheckInspections={doublecheckInspections}
          doublecheckFailureCodes={doublecheckFailureCodes}
          zoneInspections={zoneInspections}
          workCodes={workCodes}
          translations={translations}
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowSummaryModal(false)}
          isSubmitting={isSaving}
        />
      )}

      <ConfirmModal
        isOpen={showSuccessModal}
        title={tCommon('status.successTitle')}
        message={successMessage}
        confirmText={tCommon('actions.ok')}
        variant="success"
        hideCancel
        onConfirm={handleSuccessClose}
        onCancel={handleSuccessClose}
      />

      <ConfirmModal
        isOpen={showOfflineModal}
        title={t('workflow.offlineBlockedTitle')}
        message={t('workflow.offlineBlockedMessage')}
        confirmText={tCommon('actions.ok')}
        variant="warning"
        hideCancel
        onConfirm={() => setShowOfflineModal(false)}
        onCancel={() => setShowOfflineModal(false)}
      />

      <InspectionReportValidationError
        isOpen={validationErrors.length > 0}
        errors={validationErrors}
        onClose={() => setValidationErrors([])}
        onErrorClick={handleErrorClick}
      />

      <ConfirmModal
        isOpen={errorMessage !== null}
        title={tCommon('status.errorTitle')}
        message={errorMessage || ''}
        confirmText={tCommon('actions.ok')}
        variant="danger"
        hideCancel
        onConfirm={() => { setErrorMessage(null); setErrorCtx(undefined); }}
        onCancel={() => { setErrorMessage(null); setErrorCtx(undefined); }}
        errorContext={errorCtx}
      />

      {/* The ONLY place requestLocationPermission() is reached: the tech taps
          "Allow location" here, after reading what location is used for. Never
          opened by the tracker itself and never called on mount. */}
      <ConfirmModal
        isOpen={SHOW_LOCATION_PERMISSION_UI && showLocationPrompt}
        title={t('locationPrompt.title')}
        message={`${t('locationPrompt.why', {
          defaultValue:
            'Location shows your manager that you were on site and puts a map pin on the photos you take. It is only recorded while an inspection is open.',
        })} ${t('locationPrompt.message')}`}
        confirmText={t('locationPrompt.allow')}
        cancelText={t('locationPrompt.notNow')}
        variant="info"
        isLoading={locationPromptLoading}
        loadingText={t('locationPrompt.requesting')}
        onConfirm={handleEnableLocation}
        onCancel={handleDismissLocationPrompt}
      />
    </div>
  );
}
